import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, Save, X, AlertCircle, CheckCircle, Camera, File } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { archiveService } from '../services/archive.service';
import { commissionService } from '../services/commission.service';
import Header from '../components/Header';
import ConfirmModal from '../components/ConfirmModal';

const AddDocument = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [commissions, setCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [selectedCommissionWithMembers, setSelectedCommissionWithMembers] = useState(null);
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: '',
    message: '',
  });

  // Récupérer l'ID de commission depuis l'URL
  const commissionIdFromUrl = searchParams.get('commissionId');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    commissionId: commissionIdFromUrl || '',
    status: 'DRAFT',
    location: '',
    tags: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchCommissions();
  }, []);

  // Charger les membres de la commission quand elle change
  useEffect(() => {
    const loadCommissionMembers = async () => {
      if (formData.commissionId) {
        try {
          const membersData = await commissionService.getCommissionMembers(formData.commissionId);
          const membersArray = membersData?.content || membersData || [];
          setSelectedCommissionWithMembers({
            id: parseInt(formData.commissionId),
            members: membersArray,
          });
        } catch (error) {
          console.error('Erreur lors du chargement des membres:', error);
          setSelectedCommissionWithMembers(null);
        }
      } else {
        setSelectedCommissionWithMembers(null);
      }
    };

    loadCommissionMembers();
  }, [formData.commissionId]);

  const fetchCommissions = async () => {
    try {
      const data = await commissionService.getActiveCommissions();
      setCommissions(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commissions:', error);
    } finally {
      setLoadingCommissions(false);
    }
  };

  // Vérifier si l'utilisateur peut créer des archives dans une commission
  const canManageCommission = (commission) => {
    if (!user || !commission) {
      return false;
    }

    // Les administrateurs ont tous les droits
    if (isAdmin()) {
      return true;
    }

    // Vérifier si commission.members existe et est un tableau
    if (!commission.members || !Array.isArray(commission.members)) {
      return false;
    }

    // Vérifier si l'utilisateur est membre de la commission
    const isMember = commission.members.some((member) => {
      return member.user?.id === user.id ||
             member.userId === user.id ||
             member.id === user.id;
    });

    return isMember;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Le titre doit contenir au moins 3 caractères';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'La description doit contenir au moins 10 caractères';
    }

    if (!formData.commissionId) {
      newErrors.commissionId = 'La commission est requise';
    }

    if (!selectedFile) {
      newErrors.file = 'Veuillez sélectionner un fichier';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    // Vérifier le type de fichier selon les extensions autorisées par le backend
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'mp3', 'zip', 'rar'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();

    if (!allowedExtensions.includes(fileExtension)) {
      setErrors((prev) => ({
        ...prev,
        file: `Type de fichier non supporté. Formats acceptés: ${allowedExtensions.join(', ')}`,
      }));
      return;
    }

    // Vérifier la taille (max 50MB selon le backend)
    if (file.size > 50 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: 'Le fichier est trop volumineux (max 50MB)',
      }));
      return;
    }

    setSelectedFile(file);
    setErrors((prev) => ({ ...prev, file: '' }));

    // Créer un aperçu pour les images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Vérifier les permissions avant de créer l'archive
    if (!canManageCommission(selectedCommissionWithMembers)) {
      setErrorModal({
        show: true,
        title: 'Accès refusé',
        message: 'Vous n\'avez pas le droit de créer une archive dans cette commission. Seuls les membres de la commission et les administrateurs peuvent ajouter des archives.',
      });
      return;
    }

    setLoading(true);

    try {
      // Créer un FormData pour l'upload de fichier
      const formDataToSend = new FormData();

      // Ajouter chaque champ individuellement au FormData
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('archiveDate', new Date().toISOString().split('T')[0]); // Date du jour

      if (formData.location.trim()) {
        formDataToSend.append('location', formData.location.trim());
      }

      formDataToSend.append('author', `${user.firstName} ${user.lastName}`); // Utilisateur connecté
      formDataToSend.append('commissionId', formData.commissionId);
      formDataToSend.append('status', formData.status);

      // Ajouter les tags
      const tags = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag);

      if (tags.length > 0) {
        tags.forEach((tag) => {
          formDataToSend.append('tags', tag);
        });
      }

      // Ajouter le fichier
      formDataToSend.append('file', selectedFile);

      await archiveService.createArchive(formDataToSend);

      setSuccess(true);
      setTimeout(() => {
        navigate('/documents');
      }, 1500);
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);

      if (error.fields) {
        setErrors(error.fields);
      } else {
        alert(error.message || "Erreur lors de l'ajout de l'archive");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      commissionId: '',
      status: 'DRAFT',
      location: '',
      tags: '',
    });
    setSelectedFile(null);
    setFilePreview(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const getFileIcon = (file) => {
    if (!file) return <File className="w-6 h-6" />;

    const fileName = file.name.toLowerCase();

    if (fileName.endsWith('.pdf')) return '📄';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return '📝';
    if (fileName.endsWith('.txt')) return '📃';
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return '📊';
    if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png') || fileName.endsWith('.gif')) return '🖼️';
    if (fileName.endsWith('.mp4')) return '🎥';
    if (fileName.endsWith('.mp3')) return '🎵';
    if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) return '📦';
    return '📎';
  };

  const ErrorModalComponent = () => (
    <ConfirmModal
      isOpen={errorModal.show}
      onClose={() => setErrorModal({ show: false, title: '', message: '' })}
      onConfirm={() => setErrorModal({ show: false, title: '', message: '' })}
      title={errorModal.title}
      message={errorModal.message}
      confirmText="Compris"
      variant="danger"
      cancelText=""
    />
  );

  if (success) {
    return (
      <div>
        <Header title="Ajouter une archive" />
        <div className="px-4 md:px-8">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center mt-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Archive créée !</h2>
            <p className="text-gray-600 mb-6">
              L'archive a été créée avec succès. Redirection...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          </div>
        </div>
        <ErrorModalComponent />
      </div>
    );
  }

  return (
    <div>
      <Header title="Ajouter une archive" />

      <div className="px-4 md:px-8">
        <button
          onClick={() => navigate('/documents')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Retour aux archives</span>
        </button>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Nouvelle archive</h2>
              <p className="text-gray-600 mt-1">
                Remplissez les informations ci-dessous pour créer une nouvelle archive
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre de l'archive *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Réunion Commission Culturelle - Janvier 2026"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Commission et Statut */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission *
                    </label>
                    <select
                      name="commissionId"
                      value={formData.commissionId}
                      onChange={handleChange}
                      disabled={loadingCommissions}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 ${
                        errors.commissionId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner une commission</option>
                      {commissions.map((commission) => (
                        <option key={commission.id} value={commission.id}>
                          {commission.name}
                        </option>
                      ))}
                    </select>
                    {errors.commissionId && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.commissionId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="PUBLISHED">Publié</option>
                      <option value="DRAFT">Brouillon</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Description détaillée de l'archive..."
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description.length} caractères
                  </p>
                </div>

                {/* Lieu */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu (optionnel)
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: Rennes, France"
                  />
                </div>

                {/* Upload de fichier */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document / Fichier *
                  </label>

                  {!selectedFile && (
                    <div className="space-y-3">
                      {/* Bouton Upload fichier */}
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-3 px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                        >
                          <Upload className="w-6 h-6 text-gray-400" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-700">
                              Choisir un fichier
                            </p>
                            <p className="text-xs text-gray-500">
                              PDF, Word, TXT, Images, Vidéo, Audio, ZIP (max 50MB)
                            </p>
                          </div>
                        </button>
                      </div>

                      {/* Bouton Prendre photo (mobile) */}
                      <div className="md:hidden">
                        <input
                          type="file"
                          ref={cameraInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-primary-50 border-2 border-primary-300 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          <Camera className="w-6 h-6 text-primary-600" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-primary-700">
                              Prendre une photo
                            </p>
                            <p className="text-xs text-primary-600">
                              Utiliser l'appareil photo
                            </p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Aperçu du fichier sélectionné */}
                  {selectedFile && (
                    <div className="border-2 border-primary-300 rounded-lg p-4 bg-primary-50">
                      <div className="flex items-start gap-4">
                        {filePreview ? (
                          <img
                            src={filePreview}
                            alt="Aperçu"
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-4xl">
                            {getFileIcon(selectedFile)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-transitions"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  )}

                  {errors.file && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.file}
                    </p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Ex: culturel, 2026, réunion, janvier"
                  />
                </div>

                {/* Info automatique */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ <strong>Informations automatiques :</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4">
                    <li>• Date : Aujourd'hui ({new Date().toLocaleDateString('fr-FR')})</li>
                    <li>• Auteur : {user?.firstName} {user?.lastName}</li>
                  </ul>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Création en cours...</span>
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      <span className="font-medium">Enregistrer l'archive</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <X size={20} />
                  <span>Réinitialiser</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ErrorModalComponent />
    </div>
  );
};

export default AddDocument;
